'use client';

import { ALL_STATES } from '@/constants/location_states';
import { decodeFuelItemParam, toFuelItemParam } from '@/lib/fuel';
import { toCleanSlug, toInternalDistrictSlug, toInternalStateSlug } from '@/lib/location_slug_mapper';
import { useLazyAllDistricts } from '@/hooks/useLazyAllDistricts';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

interface FuelLocationFilterProps {
  itemId: string;
  initialStateSlug: string;
  initialCitySlug: string;
}

export default function FuelLocationFilter({
  itemId,
  initialStateSlug,
  initialCitySlug,
}: FuelLocationFilterProps) {
  const router = useRouter();
  const initialStateInternal = toInternalStateSlug(initialStateSlug) || '';
  const initialCityInternal =
    initialStateInternal && initialCitySlug
      ? toInternalDistrictSlug({
          stateInternalSlug: initialStateInternal,
          districtParam: initialCitySlug,
        }) || ''
      : '';

  const [stateSlug, setStateSlug] = useState(initialStateInternal);
  const [citySlug, setCitySlug] = useState(initialCityInternal);
  const [isExpanded, setIsExpanded] = useState(false);
  const allDistricts = useLazyAllDistricts();

  const stateOptions = useMemo(
    () =>
      Object.entries(ALL_STATES).map(([slug, state]) => ({
        slug,
        label: state.inline_label || state.label,
        code: state.value,
      })),
    [],
  );

  const selectedStateCode =
    stateOptions.find((state) => state.slug === stateSlug)?.code || '';

  const cityOptions = useMemo(() => {
    if (!selectedStateCode || !allDistricts) {
      return [];
    }
    return Object.entries(allDistricts[selectedStateCode] || {}).map(([slug, city]) => ({
      slug,
      label: city.label,
    }));
  }, [selectedStateCode, allDistricts]);

  const onApply = () => {
    if (!stateSlug || !citySlug) {
      return;
    }
    const normalizedItemParam = toFuelItemParam(decodeFuelItemParam(itemId));
    const cleanState = toCleanSlug(stateSlug);
    const cleanCity = toCleanSlug(citySlug);
    router.push(`/fuel/${normalizedItemParam}/${cleanState}/${cleanCity}`);
  };

  return (
    <section className='mt-4 rounded-xl bg-white'>
      <button
        type='button'
        className='flex w-full items-center justify-between rounded-md border px-3 py-2 text-left'
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-controls='fuel-filter-panel'
      >
        <span className='text-sm font-semibold'>Filter by state and district</span>
        <span className='text-base'>{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded ? (
        <div id='fuel-filter-panel' className='mt-3 space-y-3'>
          <div className='grid gap-3 md:grid-cols-2'>
            <select
              className='rounded-md border px-3 py-2 text-sm'
              value={stateSlug}
              onChange={(event) => {
                setStateSlug(event.target.value);
                setCitySlug('');
              }}
            >
              <option value=''>Select state</option>
              {stateOptions.map((state) => (
                <option key={state.slug} value={state.slug}>
                  {state.label}
                </option>
              ))}
            </select>

            <select
              className='rounded-md border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-[#f7ede7]'
              value={citySlug}
              disabled={!stateSlug}
              onChange={(event) => setCitySlug(event.target.value)}
            >
              <option value=''>Select district</option>
              {cityOptions.map((city) => (
                <option key={city.slug} value={city.slug}>
                  {city.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type='button'
            className='rounded-md bg-[#a53a22] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#caa296]'
            disabled={!stateSlug || !citySlug}
            onClick={onApply}
          >
            Apply
          </button>
        </div>
      ) : null}
    </section>
  );
}
